<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalService',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
      default: null,
    },
    rental: {
      type: Object,
      required: false,
      default: null,
    },
    meter: {
      type: Object,
      required: false,
      default: null,
    },
    preventEditRental: {
      type: Boolean,
      required: false,
      default: false,
    },
    preventEditMeter: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignToRental: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignToMeter: {
      type: Boolean,
      required: false,
      default: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const units = ref(null);
  const formName = ref('rentalService');
  const formId = ref(null);
  const labelAlign = ref('right');
  const isReadOnly = ref(null);
  const tariffs = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_2070266589801014_dataSource = computed(() => ({
    name: 'suggest_meter',
  }));
  const b_5074589820235598_dataSource = computed(() => ({
    name: 'suggest_product',
    parameters: {
      product_types: 'service',
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    units: units.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    isReadOnly: isReadOnly.value,
    tariffs: tariffs.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2070266589801014_dataSource: b_2070266589801014_dataSource.value,
    b_5074589820235598_dataSource: b_5074589820235598_dataSource.value,
  }));

  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('read_rental_service', {
          parameters: { id: props.oid },
        })
      : $fn.value.fetch('get_rental_service_defaults', {
          parameters: {
            meter_id: props.meter ? props.meter.id : null,
            rental_id: props.rental ? props.rental.id : null,
          },
        });
  };
  const $$load_units = async () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_isReadOnly = async () => {
    return item.value.locked;
  };
  const $$load_tariffs = async () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'units' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isReadOnly', dependsOn: ['item'] },
      { name: 'tariffs' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2070266589801014_dataSource', nodeId: '2070266589801014' },
      { name: 'b_5074589820235598_dataSource', nodeId: '5074589820235598' },
    ],
    loaders: {
      item: $$load_item,
      units: $$load_units,
      formId: $$load_formId,
      isReadOnly: $$load_isReadOnly,
      tariffs: $$load_tariffs,
    },
    ux: {},
    dataRefs: {
      item,
      units,
      formName,
      formId,
      labelAlign,
      isReadOnly,
      tariffs,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2070266589801014_dataSource,
      b_5074589820235598_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: props.oid
        ? $fn.value.gettext('Edit Rental Service')
        : $fn.value.gettext('Add Rental Service'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );
  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );
  watch(
    () => props.preventEditRental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventEditRental');
    }
  );
  watch(
    () => props.preventEditMeter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventEditMeter');
    }
  );
  watch(
    () => props.assignToRental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assignToRental');
    }
  );
  watch(
    () => props.assignToMeter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assignToMeter');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ozoi89e3y_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_wmzmtf80w_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_7885dhprt_getActionArgs = async (
    $event
  ) => {
    return {
      operation: item.value.id
        ? 'update_rental_service'
        : 'create_rental_service',
      data: _.extend(item.value, { rental_id: item.value.rental.id }),
    };
  };
  const act_2070266589801014_script_q094v360r_value_callable = async (
    $event
  ) => {
    return _.extend(item.value, $event.data.response.data);
  };
  const act_2070266589801014_script_q094v360r_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2070266589801014_script_q094v360r_value_callable($event);
      },
    };
  };
  const act_2070266589801014_request_ok12qvq7l_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'get_rental_service_defaults',
      data: {
        rental_id: props.rental ? props.rental.id : item.value.rental_id,
        meter_id: item.value.meter.id,
      },
    };
  };
  const act_5074589820235598_setData_79cvb7j2v_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: item.value.service.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3322283983417540_closeModal_03ihuukay_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_5419443714917073_closeModal_xtse5j06k_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2167826738365259_closeModal_xjw7vle9i_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_ozoi89e3y =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ozoi89e3y_getActionArgs,
    });
  const inst_evh_7315092382398562_close_brlw2eu95 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ozoi89e3y,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_brlw2eu95],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_9q7ythlns =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i2ks1l9ii =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9q7ythlns,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i2ks1l9ii],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_mhwe78vh3 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_eltjj7vi1 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_mhwe78vh3,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_eltjj7vi1],
    event: 'close',
  });
  const act_1040167445267876_closeModal_wmzmtf80w =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_wmzmtf80w_getActionArgs,
    });
  const inst_evh_1040167445267876_success_crtfkrfa4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_wmzmtf80w,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_7885dhprt = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_7885dhprt_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_crtfkrfa4],
  });
  const inst_evh_1040167445267876_submit_bv9d9ttbz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_7885dhprt,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_bv9d9ttbz],
    event: 'submit',
  });
  const act_2070266589801014_script_q094v360r = new actionTypes.ScriptAction({
    actionArgs: act_2070266589801014_script_q094v360r_getActionArgs,
  });
  const inst_evh_2070266589801014_success_irfa8l56d =
    new actionTypes.EventHandlerImpl({
      action: act_2070266589801014_script_q094v360r,
      event: 'success',
      displayName: 'script',
    });
  const act_2070266589801014_request_ok12qvq7l = new actionTypes.RequestAction({
    actionArgs: act_2070266589801014_request_ok12qvq7l_getActionArgs,
    displayName: 'getDefaultsOnMeterSelected',
    events: [inst_evh_2070266589801014_success_irfa8l56d],
  });
  const inst_evh_2070266589801014_optionSelected_wyqfr3d01 =
    new actionTypes.EventHandlerImpl({
      action: act_2070266589801014_request_ok12qvq7l,
      event: 'optionSelected',
      displayName: 'getDefaultsOnMeterSelected',
    });
  const inst_evh_2070266589801014_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2070266589801014_optionSelected_wyqfr3d01],
      event: 'optionSelected',
    });
  const act_5074589820235598_setData_79cvb7j2v = new actionTypes.SetDataAction({
    actionArgs: act_5074589820235598_setData_79cvb7j2v_getActionArgs,
  });
  const inst_evh_5074589820235598_optionSelected_wn7bsmsib =
    new actionTypes.EventHandlerImpl({
      action: act_5074589820235598_setData_79cvb7j2v,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_5074589820235598_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5074589820235598_optionSelected_wn7bsmsib],
      event: 'optionSelected',
    });
  const act_3322283983417540_closeModal_03ihuukay =
    new actionTypes.CloseModalAction({
      actionArgs: act_3322283983417540_closeModal_03ihuukay_getActionArgs,
    });
  const inst_evh_3322283983417540_success_nujbfpnf5 =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeModal_03ihuukay,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_3322283983417540_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3322283983417540_success_nujbfpnf5],
    event: 'success',
  });
  const act_5419443714917073_closeModal_xtse5j06k =
    new actionTypes.CloseModalAction({
      actionArgs: act_5419443714917073_closeModal_xtse5j06k_getActionArgs,
    });
  const inst_evh_5419443714917073_success_knp24md6u =
    new actionTypes.EventHandlerImpl({
      action: act_5419443714917073_closeModal_xtse5j06k,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_5419443714917073_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5419443714917073_success_knp24md6u],
    event: 'success',
  });
  const act_2167826738365259_closeModal_xjw7vle9i =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_xjw7vle9i_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_b3bexekad =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_xjw7vle9i,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_b3bexekad],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_brlw2eu95,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_i2ks1l9ii,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_eltjj7vi1,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_crtfkrfa4,
    inst_evh_1040167445267876_submit_bv9d9ttbz,
    submit: inst_evh_1040167445267876_submit_bv9d9ttbz,
    inst_evh_1040167445267876_submit,
    inst_evh_2070266589801014_success_irfa8l56d,
    inst_evh_2070266589801014_optionSelected_wyqfr3d01,
    getDefaultsOnMeterSelected:
      inst_evh_2070266589801014_optionSelected_wyqfr3d01,
    inst_evh_2070266589801014_optionSelected,
    inst_evh_5074589820235598_optionSelected_wn7bsmsib,
    inst_evh_5074589820235598_optionSelected,
    inst_evh_3322283983417540_success_nujbfpnf5,
    inst_evh_3322283983417540_success,
    inst_evh_5419443714917073_success_knp24md6u,
    inst_evh_5419443714917073_success,
    inst_evh_2167826738365259_clickPreventStop_b3bexekad,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_brlw2eu95 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_brlw2eu95.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_i2ks1l9ii = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i2ks1l9ii.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_eltjj7vi1 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_eltjj7vi1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_crtfkrfa4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_crtfkrfa4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_bv9d9ttbz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_bv9d9ttbz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_success_irfa8l56d = async (event, scope) => {
    return await actions.inst_evh_2070266589801014_success_irfa8l56d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_optionSelected_wyqfr3d01 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2070266589801014_optionSelected_wyqfr3d01.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2070266589801014_optionSelected = async (event, scope) => {
    return await actions.inst_evh_2070266589801014_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_optionSelected_wn7bsmsib = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5074589820235598_optionSelected_wn7bsmsib.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_optionSelected = async (event, scope) => {
    return await actions.inst_evh_5074589820235598_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success_nujbfpnf5 = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success_nujbfpnf5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success_knp24md6u = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success_knp24md6u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_b3bexekad = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_b3bexekad.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalService"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :schema="oid == null ? 'CreateRentalService' : 'UpdateRentalService'"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <div v-if="assignToRental">
          <ItpFormGroup
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            name="rental"
            :label="pgettext('RentalService', 'Rental')"
            required
          >
            <UcRentalSelector
              name="rental"
              :item="item"
              path="rental"
              :readonly="isReadOnly || preventEditRental"
              required
            >
            </UcRentalSelector>
          </ItpFormGroup>
          <hr />
        </div>
        <ItpFormGroup
          name="meter"
          :label="pgettext('RentalService', 'Meter')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          required
        >
          <ItpSelect2
            name="meter"
            v-model="item.meter"
            :value="item.meter"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.meter =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_2070266589801014_dataSource"
            :initialOptions="item.meter ? [item.meter] : []"
            valueField="id"
            textField="serial_number"
            bind-object
            use-option-template-for-selected-option
            optionTemplate='<UcSuggestMeterOption :option="option"></UcSuggestMeterOption>'
            required
            :readonly="preventEditMeter || isReadOnly"
            @optionSelected="evh_2070266589801014_optionSelected($event, {})"
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="meter_factor"
          :label="pgettext('RentalService', 'Meter factor')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            type="number"
            name="meter_factor"
            v-model="item.meter_factor"
            :value="item.meter_factor"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.meter_factor =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="service"
          :label="pgettext('RentalService', 'Service')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          required
        >
          <ItpSelect2
            name="service"
            v-model="item.service"
            :value="item.service"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.service =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_5074589820235598_dataSource"
            bind-object
            valueField="id"
            textField="name"
            :readonly="isReadOnly"
            required
            @optionSelected="evh_5074589820235598_optionSelected($event, {})"
          >
          </ItpSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff_code"
          :label="pgettext('RentalService', 'Tariff')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="tariffs && tariffs.length"
        >
          <ItpFormSelect2
            name="tariff_code"
            v-model="item.tariff_code"
            :value="item.tariff_code"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.tariff_code =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="tariffs"
            clearable
            valueField="code"
            textField="code,name"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_unit"
          :label="pgettext('RentalService', 'Billing unit')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSelect2
            name="billing_unit"
            v-model="item.billing_unit"
            :value="item.billing_unit"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.billing_unit =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="units"
            valueField="symbol"
            textField="symbol"
            :readonly="isReadOnly"
            required
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="non_billable"
          :label="pgettext('RentalService', 'Non billable')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="non_billable"
            inline
            v-model="item.non_billable"
            :value="item.non_billable"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.non_billable =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('RentalService', 'Validity')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="row"
        >
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_from"
              :placeholder="pgettext('InputPlaceholder', 'Valid from')"
              v-model="item.valid_from"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="valid_to"
              :placeholder="pgettext('InputPlaceholder', 'Valid to')"
              v-model="item.valid_to"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="inactive"
          :label="pgettext('RentalService', 'Inactive')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="inactive"
            inline
            v-model="item.inactive"
            :value="item.inactive"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.inactive =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="notes"
          :label="pgettext('RentalService', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="note"
            v-model="item.notes"
            :value="item.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto" v-if="oid && !item.locked">
          <BtnDeleteResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            @success="evh_3322283983417540_success($event, {})"
          >
          </BtnDeleteResource>
          <BtnLockResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            class="ms-3"
            @success="evh_5419443714917073_success($event, {})"
          >
          </BtnLockResource>
        </ItpBox>
        <ItpButton
          :text="pgettext('ModalButton', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="!item.locked"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('ModalButton', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
